import * as THREE from 'three';

class Window {
  constructor(wall, positionOnWall = 0.5, width = 1.2, height = 1.4, sillHeight = 0.9) {
    this.id = this.generateId();
    this.wall = wall;
    this.positionOnWall = positionOnWall;
    this.width = width;
    this.height = height;
    this.sillHeight = sillHeight;
    this.mesh = null;
    this.frameMesh = null;
    this.leftSash = null;
    this.rightSash = null;

    this.isOpen = false;
    this.openAngle = 0;
    this.isSelected = false;
    this.isFlipped = false;
  }

  generateId() {
    return 'window_' + Math.random().toString(36).substr(2, 9);
  }

  getWorldPosition() {
    return new THREE.Vector3().lerpVectors(
      this.wall.start,
      this.wall.end,
      this.positionOnWall
    );
  }

  getRotation() {
    const baseRotation = this.wall.getAngle();
    return this.isFlipped ? baseRotation + Math.PI : baseRotation;
  }

  flip() {
    this.isFlipped = !this.isFlipped;
    this.updatePosition();
    console.log('Finestra ruotata di 180°:', this.isFlipped ? 'girata' : 'normale');
  }

  createMesh() {
    this.mesh = new THREE.Group();

    this.createFrame();
    this.createSashes();
    if (this.sillHeight > 0) this.createSill();

    const position = this.getWorldPosition();
    this.mesh.position.copy(position);
    this.mesh.position.y = this.sillHeight;
    this.mesh.rotation.y = this.getRotation();
    this.mesh.userData.window = this;

    return this.mesh;
  }

  createFrame() {
    const pvcMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      roughness: 0.4,
      metalness: 0.1
    });

    const frameThickness = 0.07;
    const frameDepth = this.wall.thickness + 0.04;

    const topFrame    = new THREE.BoxGeometry(this.width + frameThickness * 2, frameThickness, frameDepth);
    const sideFrame   = new THREE.BoxGeometry(frameThickness, this.height, frameDepth);
    const bottomFrame = new THREE.BoxGeometry(this.width + frameThickness * 2, frameThickness, frameDepth);

    const top = new THREE.Mesh(topFrame, pvcMaterial);
    top.position.set(0, this.height + frameThickness / 2, 0);
    top.castShadow = false;
    top.receiveShadow = false;

    const left = new THREE.Mesh(sideFrame, pvcMaterial);
    left.position.set(-this.width / 2 - frameThickness / 2, this.height / 2, 0);
    left.castShadow = false;
    left.receiveShadow = false;

    const right = new THREE.Mesh(sideFrame, pvcMaterial);
    right.position.set(this.width / 2 + frameThickness / 2, this.height / 2, 0);
    right.castShadow = false;
    right.receiveShadow = false;

    this.frameMesh = new THREE.Group();

    // La porta finestra (sillHeight = 0) non ha il telaio inferiore
    if (this.sillHeight > 0) {
      const bottom = new THREE.Mesh(bottomFrame, pvcMaterial);
      bottom.position.set(0, -frameThickness / 2, 0);
      bottom.castShadow = false;
      bottom.receiveShadow = false;
      this.frameMesh.add(bottom);
    }

    this.frameMesh.add(top, left, right);
    this.mesh.add(this.frameMesh);
  }

  createSashes() {
    const pvcMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      roughness: 0.4,
      metalness: 0.1
    });

    const glassMaterial = new THREE.MeshPhysicalMaterial({
      color: 0x88ccff,
      transparent: true,
      opacity: 0.25,
      roughness: 0.05,
      metalness: 0,
      transmission: 0.95,
      thickness: 0.5,
      envMapIntensity: 1.0,
      clearcoat: 1.0,
      clearcoatRoughness: 0.05
    });

    const sashWidth           = this.width / 2;
    const sashFrameThickness  = 0.04;
    const sashThickness       = 0.05;
    const glassThickness      = 0.02;

    // ANTA DESTRA
    this.rightSash = new THREE.Group();

    const rightTopGeom    = new THREE.BoxGeometry(sashWidth, sashFrameThickness, sashThickness);
    const rightTop        = new THREE.Mesh(rightTopGeom, pvcMaterial);
    rightTop.position.set(0, this.height / 2 - sashFrameThickness / 2, 0);
    rightTop.castShadow   = false;

    const rightBottomGeom = new THREE.BoxGeometry(sashWidth, sashFrameThickness, sashThickness);
    const rightBottom     = new THREE.Mesh(rightBottomGeom, pvcMaterial);
    rightBottom.position.set(0, -this.height / 2 + sashFrameThickness / 2, 0);
    rightBottom.castShadow = false;

    const rightLeftGeom   = new THREE.BoxGeometry(sashFrameThickness, this.height - sashFrameThickness * 2, sashThickness);
    const rightLeft       = new THREE.Mesh(rightLeftGeom, pvcMaterial);
    rightLeft.position.set(-sashWidth / 2 + sashFrameThickness / 2, 0, 0);
    rightLeft.castShadow  = false;

    const rightRightGeom  = new THREE.BoxGeometry(sashFrameThickness, this.height - sashFrameThickness * 2, sashThickness);
    const rightRight      = new THREE.Mesh(rightRightGeom, pvcMaterial);
    rightRight.position.set(sashWidth / 2 - sashFrameThickness / 2, 0, 0);
    rightRight.castShadow = false;

    const rightGlassGeom  = new THREE.BoxGeometry(
      sashWidth - sashFrameThickness * 2,
      this.height - sashFrameThickness * 2,
      glassThickness
    );
    const rightGlass = new THREE.Mesh(rightGlassGeom, glassMaterial);
    rightGlass.receiveShadow = false;

    const handleGeometry = new THREE.CylinderGeometry(0.012, 0.012, 0.1, 8);
    const handleMaterial = new THREE.MeshStandardMaterial({
      color: 0xaaaaaa,
      roughness: 0.3,
      metalness: 0.8
    });

    const rightHandle = new THREE.Mesh(handleGeometry, handleMaterial);
    rightHandle.rotation.z = 0;
    rightHandle.geometry.translate(0, -0.05, 0);
    rightHandle.position.set(
      sashWidth / 2 - sashFrameThickness / 2,
      0,
      sashThickness / 2 - 0.05
    );
    rightHandle.castShadow = false;

    this.rightSash.add(rightTop, rightBottom, rightLeft, rightRight, rightGlass, rightHandle);

    // ANTA SINISTRA
    this.leftSash = new THREE.Group();

    const leftTopGeom    = new THREE.BoxGeometry(sashWidth, sashFrameThickness, sashThickness);
    const leftTop        = new THREE.Mesh(leftTopGeom, pvcMaterial);
    leftTop.position.set(0, this.height / 2 - sashFrameThickness / 2, 0);
    leftTop.castShadow   = false;

    const leftBottomGeom = new THREE.BoxGeometry(sashWidth, sashFrameThickness, sashThickness);
    const leftBottom     = new THREE.Mesh(leftBottomGeom, pvcMaterial);
    leftBottom.position.set(0, -this.height / 2 + sashFrameThickness / 2, 0);
    leftBottom.castShadow = false;

    const leftRightGeom  = new THREE.BoxGeometry(sashFrameThickness, this.height - sashFrameThickness * 2, sashThickness);
    const leftRight      = new THREE.Mesh(leftRightGeom, pvcMaterial);
    leftRight.position.set(sashWidth / 2 - sashFrameThickness / 2, 0, 0);
    leftRight.castShadow = false;

    const leftLeftGeom   = new THREE.BoxGeometry(sashFrameThickness, this.height - sashFrameThickness * 2, sashThickness);
    const leftLeft       = new THREE.Mesh(leftLeftGeom, pvcMaterial);
    leftLeft.position.set(-sashWidth / 2 + sashFrameThickness / 2, 0, 0);
    leftLeft.castShadow  = false;

    const leftGlassGeom  = new THREE.BoxGeometry(
      sashWidth - sashFrameThickness * 2,
      this.height - sashFrameThickness * 2,
      glassThickness
    );
    const leftGlass = new THREE.Mesh(leftGlassGeom, glassMaterial);
    leftGlass.receiveShadow = false;

    this.leftSash.add(leftTop, leftBottom, leftRight, leftLeft, leftGlass);

    // PIVOT GROUPS
    this.rightPivot = new THREE.Group();
    this.rightPivot.position.set(-this.width / 2, this.height, 0);
    this.rightSash.position.set(sashWidth / 2, -this.height / 2, 0);
    this.rightPivot.add(this.rightSash);

    this.leftPivot = new THREE.Group();
    this.leftPivot.position.set(this.width / 2, this.height, 0);
    this.leftSash.position.set(-sashWidth / 2, -this.height / 2, 0);
    this.leftPivot.add(this.leftSash);

    this.mesh.add(this.rightPivot, this.leftPivot);
  }

  createSill() {
    const sillMaterial = new THREE.MeshStandardMaterial({
      color: 0xfce5cd,
      roughness: 0.5,
      metalness: 0.1
    });

    const sillGeometry = new THREE.BoxGeometry(
      this.width + 0.2,
      0.06,
      this.wall.thickness + 0.2
    );

    const sill = new THREE.Mesh(sillGeometry, sillMaterial);
    sill.position.set(0, -0.07, 0);
    sill.castShadow = false;
    sill.receiveShadow = false;
    this.mesh.add(sill);
  }

  animateHandle(duration, callback, reverse = false) {
    if (!this.rightSash) return;

    const handle = this.rightSash.children.find(child =>
      child.geometry && child.geometry.type === 'CylinderGeometry'
    );

    if (!handle) {
      if (callback) callback();
      return;
    }

    const startAngle = reverse ? -Math.PI / 2 : 0;
    const endAngle   = reverse ? 0 : -Math.PI / 2;
    const startTime  = performance.now();

    const animate = (currentTime) => {
      const elapsed  = (currentTime - startTime) / 1000;
      const progress = Math.min(elapsed / duration, 1);
      const eased    = 1 - Math.pow(1 - progress, 3);

      handle.rotation.z = startAngle + (endAngle - startAngle) * eased;

      window.requestRender?.();

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        if (callback) callback();
      }
    };

    requestAnimationFrame(animate);
  }

  open(duration = 1.2) {
    if (this.isOpen) return;
    this.isOpen = true;

    this.animateHandle(0.3, () => {
      const targetAngle = -Math.PI / 2;
      const startTime   = performance.now();

      const animate = (currentTime) => {
        const elapsed  = (currentTime - startTime) / 1000;
        const progress = Math.min(elapsed / duration, 1);
        const eased    = 1 - Math.pow(1 - progress, 3);

        this.openAngle = eased * targetAngle;
        this.leftPivot.rotation.y  =  this.openAngle;
        this.rightPivot.rotation.y = -this.openAngle;

        window.requestRender?.();

        if (progress < 1) requestAnimationFrame(animate);
      };

      requestAnimationFrame(animate);
    });
  }

  close(duration = 1.0) {
    if (!this.isOpen) return;
    this.isOpen = false;

    const startAngle = this.openAngle;
    const startTime  = performance.now();

    const animate = (currentTime) => {
      const elapsed  = (currentTime - startTime) / 1000;
      const progress = Math.min(elapsed / duration, 1);
      const eased    = 1 - Math.pow(1 - progress, 3);

      this.openAngle = startAngle * (1 - eased);
      this.leftPivot.rotation.y  =  this.openAngle;
      this.rightPivot.rotation.y = -this.openAngle;

      window.requestRender?.();

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.openAngle = 0;
        this.leftPivot.rotation.y  = 0;
        this.rightPivot.rotation.y = 0;
        this.animateHandle(0.3, null, true);
      }
    };

    requestAnimationFrame(animate);
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  select() {
    this.isSelected = true;
    if (this.mesh) {
      this.mesh.traverse(child => {
        if (child.material && child.material.emissive) {
          child.material.emissive.setHex(0xddeeff);
        }
      });
    }
    window.requestRender?.();
  }

  deselect() {
    this.isSelected = false;
    if (this.mesh) {
      this.mesh.traverse(child => {
        if (child.material && child.material.emissive) {
          child.material.emissive.setHex(0x000000);
        }
      });
    }
    window.requestRender?.();
  }

  updatePosition() {
    if (!this.mesh) return;
    const position = this.getWorldPosition();
    this.mesh.position.copy(position);
    this.mesh.position.y = this.sillHeight;
    this.mesh.rotation.y = this.getRotation();
    window.requestRender?.();
  }

  getBoundingBox() {
    return {
      start:         this.positionOnWall - this.width / 2 / this.wall.getLength(),
      end:           this.positionOnWall + this.width / 2 / this.wall.getLength(),
      width:         this.width,
      verticalStart: this.sillHeight,
      verticalEnd:   this.sillHeight + this.height
    };
  }

  overlapsWithDoor(door) {
    const windowBox = this.getBoundingBox();
    const doorBox   = door.getBoundingBox();

    const horizontalOverlap = !(
      windowBox.end < doorBox.start ||
      windowBox.start > doorBox.end
    );
    const verticalOverlap = windowBox.verticalStart < door.height;

    return horizontalOverlap && verticalOverlap;
  }

  dispose() {
    if (this.mesh) {
      this.mesh.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
    }
  }
}

export default Window;